import { ConditionWrapper, Grid } from '@approvalmax/ui/src/components';
import { numberHelpers } from '@approvalmax/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import uniqueId from 'lodash/uniqueId';
import { backend, domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import moment from 'moment';
import { FC, memo, useCallback, useMemo } from 'react';
import { useLifecycles } from 'react-use';
import { useGetNetSuiteCustomer } from 'shared/data/webApp/v2';
import {
    useNetSuiteAttachmentsFieldSettings,
    useNetSuiteInvoiceFieldSettings,
    useUserBetaFeatures,
} from 'shared/hooks';

import { AttachmentsSection, RequesterInstructionSection } from '../../../components';
import FileDropProvider from '../../../components/FileDropProvider';
import { getNetSuiteContext } from '../../../selectors/netsuite/netSuiteSelectors';
import { getContext } from '../../../selectors/pageSelectors';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import { NetSuiteInvoiceItemList, NetSuiteRequestFooterLegacy, NetSuiteRequestItemsSummary } from '../components';
import { NetSuiteExtendedCustomer } from '../data/types';
import {
    calculateLineAmountsBasedOnAmount,
    convertCustomFieldsToState,
    convertIntegrationCodeToDocumentType,
    getCustomFieldsTransfer,
    mapAddress,
    setCurrencyPrecision,
    setInvoiceListItemCurrencyPrecision,
} from '../helpers';
import {
    useCurrencyPrecision,
    useCustomFields,
    useDiscountAmountValue,
    useDiscountDateValue,
    useExchangeRates,
    useResetInvoiceFormState,
    useUploadAttachments,
} from '../hooks';
import { InvoiceShippingDetailsSection } from '../InvoiceShippingDetailsSection/InvoiceShippingDetailsSection';
import { isFieldMandatory, isFieldVisible, isFutureDate } from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteCustomFieldsState, netSuiteItemLineNumberState, netSuiteShowErrorsState } from '../resources/states';
import { FieldsSection, SalesDetailsSection, ShippingDetailsSection } from './components';
import {
    getInvoiceValidationErrors,
    mapCustomerData,
    mapInvoiceItemLinesTransfer,
    parseDiscountRate,
} from './NetSuiteInvoiceRequestForm.helpers';
import { useNetSuiteInvoiceAmounts, useNetSuiteInvoiceFields } from './NetSuiteInvoiceRequestForm.hooks';
import { messages } from './NetSuiteInvoiceRequestForm.messages';
import { netSuiteInvoiceFieldValuesState, netSuiteInvoiceItemListState } from './NetSuiteInvoiceRequestForm.states';
import { Content, Root, Toolbar, ToolbarText } from './NetSuiteInvoiceRequestForm.styles';
import { NetSuiteInvoiceRequestFormProps } from './NetSuiteInvoiceRequestForm.types';

export const NetSuiteInvoiceRequestForm: FC<NetSuiteInvoiceRequestFormProps> = memo((props) => {
    const { request } = props;

    const pageContext = useSelector(getContext);
    const editingContext = useSelector(getNetSuiteContext);
    const customFieldsValues = useAtomValue(netSuiteCustomFieldsState);
    const setShowErrors = useSetAtom(netSuiteShowErrorsState);

    const fieldValues = useAtomValue(netSuiteInvoiceFieldValuesState);
    const [itemList, setItemList] = useAtom(netSuiteInvoiceItemListState);

    const { updateFieldsBulk } = useNetSuiteInvoiceFields();

    const { currencies, customFields, documentFields, items, terms } = editingContext;

    const invoiceFieldSettings = useNetSuiteInvoiceFieldSettings(documentFields);

    const { setCustomFieldValuesBulk } = useCustomFields(customFields);

    const { onUploadAttachments } = useUploadAttachments(request.id);

    const { netAmount, taxAmount, totalAmount, transactionDiscount } = useNetSuiteInvoiceAmounts();

    const resetInvoiceFormState = useResetInvoiceFormState();

    const { data: dataExchangeRates } = useExchangeRates(request.companyId, fieldValues.transactionDate);

    const setNextLineNumber = useSetAtom(netSuiteItemLineNumberState);

    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    const defaultCurrencyRate = useMemo(() => {
        const exchangeRate = dataExchangeRates?.rates.find((currency) => {
            return currency.id === fieldValues.currency?.id;
        });

        return exchangeRate?.rateValue;
    }, [dataExchangeRates, fieldValues.currency]);

    const requestCurrencyISOCode = useMemo(() => {
        return currencies.find((currency) => currency.id === fieldValues.currency?.id)?.ISOCode ?? null;
    }, [currencies, fieldValues.currency]);

    const discountAmount = useDiscountAmountValue(terms, fieldValues.terms?.id, totalAmount, fieldValues.currency);

    const discountDate = useDiscountDateValue(terms, fieldValues.terms?.id, fieldValues.transactionDate);

    const { data: netSuiteCustomer, isFetching: isFetchingGetNetSuiteCustomer } = useGetNetSuiteCustomer({
        path: {
            companyId: request.companyId,
            customerId: fieldValues.customer?.id,
            documentType: convertIntegrationCodeToDocumentType(domain.IntegrationCode.NetSuiteInvoice),
        },
    });

    const customerData: NetSuiteExtendedCustomer | null = useMemo(
        () => mapCustomerData(netSuiteCustomer),
        [netSuiteCustomer]
    );

    const attachmentsFieldSettings = useNetSuiteAttachmentsFieldSettings(documentFields);

    useLifecycles(
        () => {
            const { customFields, itemList, ...fields } = request.details;

            const transactionDate = fields.transactionDate || moment().toISOString();
            const currency = currencies.find(({ id, ISOCode }) =>
                fields.currency ? fields.currency.id === id : request.companyCurrency === ISOCode
            );

            const expandedTerms = terms.find(({ id }) => id === fieldValues.terms?.id);

            updateFieldsBulk(
                {
                    ...fields,
                    transactionDate,
                    currency,
                    terms: expandedTerms,
                    billingAddress: mapAddress(fields.billingAddress),
                    shippingAddress: mapAddress(fields.shippingAddress),
                    discountRate: numberHelpers.isNumber(fields.discountRate)
                        ? `${fields.discountRate}${fields.isDiscountRateAbsolute ? '' : '%'}`
                        : '',
                    exchangeRate:
                        request.exchangeRateSource === domain.ExchangeRateSource.Manual ? fields.exchangeRate : null,
                },
                false
            );

            if (itemList.length) {
                const maxLineNumber = Math.max(...itemList.map((item) => item.lineNumber));

                setNextLineNumber(maxLineNumber + 1);

                setItemList(
                    itemList.map((line) => {
                        const itemInfo = line.item && items.find((item) => item.id === line.item?.id);

                        const expandedLine = {
                            ...line,
                            ...calculateLineAmountsBasedOnAmount(line.amount, line.taxRate, currencyPrecision),
                            isEmpty: false,
                            checked: false,
                            defaultRate: null,
                            unitList: itemInfo?.unitList ?? [],
                            salesPrices: itemInfo?.salesPrices ?? [],
                            priceLevelList: [],
                            id: uniqueId('netSuiteInvoiceListItem-'),
                        };

                        return setInvoiceListItemCurrencyPrecision(expandedLine, currency?.precision ?? 2);
                    })
                );
            }

            if (customFields) {
                setCustomFieldValuesBulk(customFields.length > 0 ? convertCustomFieldsToState(customFields) : {});
            }
        },
        () => {
            resetInvoiceFormState();
        }
    );

    const getRequestTransfer = useCallback((): backend.Transfer<backend.transfers.RequestEditTransfer> => {
        const transfer: backend.Transfer<backend.transfers.RequestEditTransfer> = {
            requestId: request.id,
            amount: totalAmount ?? null,
            currency: currencies.find((currency) => currency.id === fieldValues.currency?.id)?.ISOCode,
            exchangeRate: fieldValues.exchangeRate || defaultCurrencyRate || 1,
            exchangeRateSource:
                typeof fieldValues.exchangeRate === 'number'
                    ? domain.ExchangeRateSource.Manual
                    : domain.ExchangeRateSource.NetSuite,
            exchangeRateDate: isFutureDate(fieldValues.transactionDate)
                ? moment.utc().format('yyyy-MM-DD')
                : moment.utc(fieldValues.transactionDate).format('yyyy-MM-DD'),
            attachments: request.attachments.map((attachment) => attachment.id),
            requestNote: request.requestNote.value,
            templateVersion: pageContext.templateVersion,
            companyId: request.companyId,
        };

        const { discountRate, isDiscountRateAbsolute } = parseDiscountRate(fieldValues.discountRate);

        transfer.netSuiteInvoiceDetails = {
            isDiscountRateAbsolute,
            discountRate: isDiscountRateAbsolute ? setCurrencyPrecision(discountRate, currencyPrecision) : discountRate,
            customerId: fieldValues.customer?.id ?? null,
            billingAddressId: fieldValues.billingAddress?.id ?? null,
            transactionReference: fieldValues.transactionReference ?? null,
            transactionDate: fieldValues.date ?? null,
            dueDate: fieldValues.dueDate ?? null,
            startDate: fieldValues.startDate ?? null,
            endDate: fieldValues.endDate ?? null,
            termsId: fieldValues.terms?.id ?? null,
            currencyId: fieldValues.currency?.id ?? null,
            exchangeRate: fieldValues.exchangeRate ?? defaultCurrencyRate ?? null,
            classId: fieldValues.class?.id ?? null,
            departmentId: fieldValues.department?.id ?? null,
            locationId: fieldValues.location?.id ?? null,
            discountItemId: fieldValues.discountItem?.id ?? null,
            memo: fieldValues.memo ?? null,
            salesRepId: fieldValues.salesRep?.id ?? null,
            opportunityId: fieldValues.opportunity?.id ?? null,
            salesEffectiveDate: fieldValues.salesEffectiveDate ?? null,
            excludeCommission: fieldValues.excludeCommissions ?? false,
            partnerId: fieldValues.partner?.id ?? null,
            leadSourceId: fieldValues.leadSource?.id ?? null,
            shippingDate: fieldValues.shipDate ?? null,
            shippingMethodId: fieldValues.shipMethod?.id ?? null,
            shippingAddressId: fieldValues.shippingAddress?.id ?? null,
            itemLines: mapInvoiceItemLinesTransfer(itemList),
            customFields: getCustomFieldsTransfer(Object.values(customFieldsValues)),
        };

        return transfer;
    }, [
        fieldValues,
        currencies,
        customFieldsValues,
        defaultCurrencyRate,
        itemList,
        request,
        totalAmount,
        currencyPrecision,
        pageContext.templateVersion,
    ]);

    const getValidationErrors = useCallback(async () => {
        setShowErrors(true);

        return getInvoiceValidationErrors({
            fields: fieldValues,
            customFieldsValues: customFieldsValues,
            customFieldsList: customFields,
            workflowId: request.template.id,
            companyCurrency: request.companyCurrency,
            fieldSettings: invoiceFieldSettings,
            requestCurrencyISOCode,
            defaultCurrencyRate,
            itemList,
            attachmentsFieldSettings,
            attachments: request.attachments,
        });
    }, [
        fieldValues,
        customFieldsValues,
        request,
        requestCurrencyISOCode,
        invoiceFieldSettings,
        defaultCurrencyRate,
        customFields,
        itemList,
        setShowErrors,
        attachmentsFieldSettings,
    ]);

    const { isBelgradeUIFilesUploadSection, isBelgradeUIShippingDetailsSection, isBelgradeUILineItemsTotalSection } =
        useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection}
            wrapperIf={(children) => (
                <FileDropProvider maxSize={pageContext.attachmentMaxSize} onUpload={onUploadAttachments}>
                    {children}
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarText>{messages.toolbarText}</ToolbarText>

                    <ActionsBlock
                        customGetRequestTransfer={getRequestTransfer}
                        customRequestValidationFn={getValidationErrors}
                    />
                </Toolbar>

                <Content>
                    <Grid gridTemplateColumns='100%' padding='0 0 40'>
                        <RequesterInstructionSection instructionText={pageContext.requesterInstruction} />

                        <FieldsSection
                            workflowId={request.template.id}
                            discountAmount={discountAmount}
                            discountDate={discountDate}
                            customerData={customerData}
                            isFetchingBillingAddresses={isFetchingGetNetSuiteCustomer}
                        />

                        <NetSuiteInvoiceItemList workflowId={request.template.id} />

                        {isBelgradeUILineItemsTotalSection ? (
                            <NetSuiteRequestItemsSummary
                                netAmount={netAmount}
                                taxAmount={taxAmount}
                                totalAmount={totalAmount}
                                transactionDiscount={transactionDiscount}
                                currency={fieldValues.currency}
                                isInvoice
                            />
                        ) : (
                            <NetSuiteRequestFooterLegacy
                                netAmount={netAmount}
                                taxAmount={taxAmount}
                                totalAmount={totalAmount}
                                transactionDiscount={transactionDiscount}
                                currency={fieldValues.currency}
                                isInvoice
                            />
                        )}

                        <SalesDetailsSection />

                        {isBelgradeUIShippingDetailsSection ? (
                            <InvoiceShippingDetailsSection />
                        ) : (
                            <ShippingDetailsSection
                                shippingAddresses={customerData?.addresses}
                                isFetchingShippingAddresses={isFetchingGetNetSuiteCustomer}
                            />
                        )}

                        {isFieldVisible(attachmentsFieldSettings.attachments) &&
                            (isBelgradeUIFilesUploadSection ? (
                                <Grid gridTemplateColumns='50%' gap={16}>
                                    <AttachmentsSection
                                        request={request}
                                        required={isFieldMandatory(attachmentsFieldSettings.attachments)}
                                    />
                                </Grid>
                            ) : (
                                <FilesSection required={isFieldMandatory(attachmentsFieldSettings.attachments)} />
                            ))}
                    </Grid>
                </Content>
            </Root>
        </ConditionWrapper>
    );
});

NetSuiteInvoiceRequestForm.displayName = 'NetSuiteInvoiceRequestForm';
